import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Users, Settings, LogOut, ChevronLeft, ChevronRight, X, LayoutDashboard, Video, FileText, Code, HelpCircle, Shield, Sliders, Sparkles, BookOpen, HardDrive, Bell, ClipboardList, Info, Briefcase, Plug, Lightbulb, Stethoscope } from 'lucide-react';
import { Button } from '../ui/button';
import BrandMark from '../BrandMark';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

// Navigation items configuration - add new pages here
const navItems = [
  { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/admin/partners', icon: Users, label: 'Partners' },
  { path: '/admin/partner-ledgers', icon: BookOpen, label: 'Partner Ledgers' },
  { path: '/admin/will-parameters', icon: FileText, label: 'Will Parameters' },
  { path: '/admin/client-features', icon: Sliders, label: 'Client Features' },
  { path: '/admin/login-features', icon: Sparkles, label: 'Login Page Features' },
  { path: '/admin/login-popup', icon: Bell, label: 'Login Popup' },
  { path: '/admin/about-us', icon: Info, label: 'About Us' },
  { path: '/admin/embed-settings', icon: Code, label: 'Embed Settings' },
  { path: '/admin/videos', icon: Video, label: 'Video Settings' },
  { path: '/admin/faqs', icon: HelpCircle, label: 'FAQs' },
  { path: '/admin/knowledge', icon: Lightbulb, label: 'Knowledge' },
  { path: '/admin/legal', icon: Shield, label: 'Legal Pages' },
  { path: '/admin/consent-logs', icon: ClipboardList, label: 'Consent Logs' },
  { path: '/admin/services', icon: Briefcase, label: 'Services' },
  { path: '/admin/backup', icon: HardDrive, label: 'Data Backup' },
  { path: '/admin/integrations', icon: Plug, label: 'Integrations' },
  { path: '/admin/diagnostics', icon: Stethoscope, label: 'Diagnostics' },
  { path: '/admin/settings', icon: Settings, label: 'Settings' },
];

const Sidebar = ({ isCollapsed, onToggle, onLogout, isMobileOpen = false, onMobileClose = () => {} }) => {
  const location = useLocation();

  // On mobile (md breakpoint), force expanded view regardless of desktop collapse state
  const effectiveCollapsed = isCollapsed; // only applies >= md

  return (
    <aside
      className={`fixed top-0 left-0 h-screen bg-slate-900 border-r border-slate-800 flex flex-col transition-transform duration-300 md:transition-all md:duration-300
        ${isMobileOpen ? 'z-[60]' : 'z-40'}
        ${effectiveCollapsed ? 'md:w-20' : 'md:w-64'}
        w-64
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
        md:translate-x-0`}
      data-testid="admin-sidebar"
    >
      {/* Logo - Fixed Height */}
      <div className={`h-16 flex items-center border-b border-slate-800 flex-shrink-0 px-6 ${effectiveCollapsed ? 'md:justify-center md:px-2' : ''}`}>
        <a href="/admin/dashboard" className={`flex items-center gap-3 ${effectiveCollapsed ? 'md:justify-center md:gap-0' : ''}`}>
          {/* Mobile + expanded-desktop view shows text */}
          <span className="md:hidden">
            <BrandMark size="md" iconOnly={false} showText={true} />
          </span>
          <span className="hidden md:flex">
            <BrandMark size="md" iconOnly={effectiveCollapsed} showText={!effectiveCollapsed} />
          </span>
        </a>
        {/* Mobile close button */}
        <button
          onClick={onMobileClose}
          className="md:hidden ml-auto p-2 -mr-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
          aria-label="Close menu"
          data-testid="admin-sidebar-close-btn"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Toggle Button - desktop only. Rendered outside mobile viewport by absolute positioning + md:flex. We also wrap in a md-only container to avoid leak through translate-x-full. */}
      <div className="hidden md:block">
        <button
          onClick={onToggle}
          className="flex absolute -right-3 top-16 w-6 h-6 bg-slate-800 border border-slate-700 rounded-full items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 transition-colors z-50"
          aria-label={effectiveCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {effectiveCollapsed ? (
            <ChevronRight className="w-4 h-4" />
          ) : (
            <ChevronLeft className="w-4 h-4" />
          )}
        </button>
      </div>

      {/* Navigation - Scrollable */}
      <nav className={`flex-1 py-4 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent px-4 ${effectiveCollapsed ? 'md:px-3' : ''}`}>
        <div className={`flex flex-col space-y-1 ${effectiveCollapsed ? 'md:items-center md:space-y-2' : ''}`}>
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <NavItem
                key={item.path}
                item={item}
                isCollapsed={effectiveCollapsed}
                isActive={isActive}
              />
            );
          })}
        </div>
      </nav>

      {/* Logout - Fixed at Bottom */}
      <div className={`border-t border-slate-800 py-3 flex-shrink-0 px-4 ${effectiveCollapsed ? 'md:px-3 md:flex md:justify-center' : ''}`}>
        {/* Expanded label variant — shown on mobile always, on desktop only when not collapsed */}
        <Button
          variant="ghost"
          onClick={onLogout}
          className={`w-full justify-start gap-3 px-4 py-3 h-12 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-xl ${effectiveCollapsed ? 'md:hidden' : ''}`}
          data-testid="admin-logout-btn"
        >
          <LogOut className="w-5 h-5" />
          <span className="font-medium">Logout</span>
        </Button>
        {/* Collapsed icon-only variant — desktop only */}
        {effectiveCollapsed && (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={onLogout}
                  className="hidden md:flex w-12 h-12 items-center justify-center rounded-xl text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-all duration-200"
                  data-testid="admin-logout-collapsed-btn"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="bg-slate-800 border-slate-700 text-white">
                Logout
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    </aside>
  );
};

// Reusable NavItem component
const NavItem = ({ item, isCollapsed, isActive }) => {
  const Icon = item.icon;

  // Expanded label row — visible on mobile always, and on desktop when NOT collapsed
  const expandedRow = (
    <NavLink
      to={item.path}
      className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 ${
        isActive
          ? 'bg-amber-500/20 text-amber-400'
          : 'text-slate-400 hover:bg-slate-800 hover:text-white'
      } ${isCollapsed ? 'md:hidden' : ''}`}
    >
      <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-amber-400' : ''}`} />
      <span className="font-medium text-sm">{item.label}</span>
    </NavLink>
  );

  // Icon-only row (desktop-only) — shown when sidebar collapsed on >= md
  const iconRow = isCollapsed ? (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <NavLink
            to={item.path}
            className={`hidden md:flex w-12 h-12 items-center justify-center rounded-xl transition-all duration-200 ${
              isActive
                ? 'bg-amber-500/20'
                : 'hover:bg-slate-800'
            }`}
          >
            <Icon
              className={`w-5 h-5 ${
                isActive ? 'text-amber-400' : 'text-slate-400 hover:text-white'
              }`}
            />
          </NavLink>
        </TooltipTrigger>
        <TooltipContent side="right" className="bg-slate-800 border-slate-700 text-white">
          {item.label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : null;

  return (
    <>
      {expandedRow}
      {iconRow}
    </>
  );
};

// Export navItems for use in other components if needed
export { navItems };
export default Sidebar;
